// @ts-strict-ignore
import classNames from 'classnames'
import { ClassValue } from 'classnames/types'
import React, { ChangeEvent, KeyboardEvent, ReactNode, useEffect, useState } from 'react'
import { Toasts } from '../toaster/Toast'

interface InputProps {
    loading?: boolean
    value?: string | number
    type?: string
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void
    className?: ClassValue
    disabled?: boolean
    placeholder?: string
    style?: style
    numeric?: boolean
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
    name?: string
    autoFocus?: boolean
    suffix?: ReactNode
    prefix?: ReactNode
    allowNegative?: boolean
}

interface style {
    [key: string]: string | number
}
export const Input = ({
    disabled = false,
    loading = false,
    value = '',
    type = 'text',
    className,
    onChange,
    placeholder,
    style,
    numeric = false,
    onKeyDown = null,
    name = '',
    autoFocus = false,
    prefix,
    suffix,
    allowNegative = false,
}: InputProps) => {
    const s = {
        border: '1px solid #dee2e6 !important',
        height: '37px',
        ...style,
    }

    const _onChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (numeric) {
            const val = e.target.value
            if (!validateNumericInput(val, allowNegative)) {
                Toasts.error(
                    'Only numeric values are allowed. Please use . (dot) as delimiter for decimal values.',
                    'invalid-numeric',
                )
                return
            }
        }

        return onChange(e)
    }

    return (
        <div className="bp4-input-group flex items-center">
            <div className="relative flex items-center">
                {prefix && <Prefix item={prefix} />}
                <input
                    autoFocus={!!autoFocus}
                    name={name}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    style={s}
                    disabled={loading || disabled}
                    type={type}
                    className={classNames(
                        'bp4-input shadow-none border-1 border-solid rounded-md border-mjGray-1000',
                        className,
                    )}
                    value={value}
                    onChange={_onChange}
                />
                {suffix && <Suffix item={suffix} />}
            </div>
        </div>
    )
}

type TextInputProps = Omit<InputProps, 'onChange'> & {
    name?: string
    autoFocus?: boolean
    onChange: (value: string, name: string) => void
}

export const TextInput = ({
    type,
    name,
    placeholder,
    disabled,
    loading,
    value,
    onChange,
    className,
    style,
    suffix,
    prefix,
    onKeyDown = null,
    autoFocus = false,
}: TextInputProps) => {
    const [innerValue, setInnerValue] = useState<string>('')

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value
        const name = event?.target?.name
        setInnerValue(newValue)
        onChange(newValue, name)
    }

    useEffect(() => {
        setInnerValue(`${value}`)
    }, [value])

    return (
        <Input
            type={type}
            name={name}
            onKeyDown={onKeyDown}
            style={style}
            className={className}
            placeholder={placeholder}
            disabled={disabled}
            loading={loading}
            value={innerValue}
            onChange={handleInputChange}
            autoFocus={!!autoFocus}
            prefix={prefix}
            suffix={suffix}
        />
    )
}

// Regular expression to match valid input (numbers and at most one dot)
function validateNumericInput(value, allowNegative: boolean) {
    console.log('validate', value, allowNegative)
    let regex
    if (allowNegative) {
        regex = /^-?[0-9]*\.?[0-9]*$/
    } else {
        regex = /^[0-9]*\.?[0-9]*$/
    }

    return regex.test(value)
}

const Prefix = ({ item }: { item: ReactNode }) => {
    return (
        <span className="cursor-default hover:bg-mjGray-400 bp4-icon rounded px-1 text-mjDark-1000 font-medium absolute left-2 top-1/2 transform -translate-y-1/2 z-50">
            {item}
        </span>
    )
}

const Suffix = ({ item }: { item: ReactNode }) => {
    return (
        <span className="cursor-default hover:bg-mjGray-400 bp4-icon rounded px-1 text-mjDark-1000 font-medium absolute right-2 top-1/2 transform -translate-y-1/2 z-50">
            {item}
        </span>
    )
}
